import { useMemo } from "react";
import { feilbudsjettering, pipelineBf } from "@/lib/dashboard-bf-calc";
import { shiftPeriod } from "@/lib/dashboard-period";
import {
  calcPersonLeaderboard,
  calcSellerBudgetAccuracy,
  calcTopProjectsByBf,
  calcWorstBudgetProjects,
  calcPipelinePerPerson,
} from "@/lib/dashboard-panels-calc";
import { usePeriodFilter, usePersonFilter } from "@/contexts/dashboard-filter-context";
import { useSalesDashboardData } from "@/hooks/use-sales-dashboard-data";
import { LeaderboardPanel } from "./LeaderboardPanel";
import { FeilbudsjetteringPanel } from "./FeilbudsjetteringPanel";
import { BestWorstProjectsPanel } from "./BestWorstProjectsPanel";
import { PipelinePanel } from "./PipelinePanel";
import { SalesUnitHourlyRatePanel } from "./SalesUnitHourlyRatePanel";
import { ProjectLeadHourlyRatePanel } from "./ProjectLeadHourlyRatePanel";
import OffersInPipePanel from "./OffersInPipePanel";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useCanAccessMenu } from "@/hooks/use-menu-permissions";

/**
 * F2 panel grid. Pulls data once, reads period + person filters, delegates all
 * aggregation to dashboard-panels-calc. Comparison panels (leaderboard,
 * per-seller, pipeline breakdown) always show the whole team and highlight the
 * filtered person; aggregate panels (totals, best/worst) react to the person
 * filter — consistent with the KPI cards.
 */
export function SalesPanels() {
  const { period } = usePeriodFilter();
  const { selectedUserId } = usePersonFilter();
  const { projects, owners, salesUnits, people, projectMeta, isLoading } = useSalesDashboardData();
  const salesUnitHourlyRateEnabled = useCanAccessMenu("timepris-selger");
  const projectLeadHourlyRateEnabled = useCanAccessMenu("timepris-prosjektleder");
  // VIB-2094: «Tilbud i pipe» hører til salgspipeline-pakken og deler flagg
  // med KPI-kortet og kanban-boardet, så én bryter styrer hele flaten.
  const offerPipelineEnabled = useFeatureFlag("feature_offer_pipeline");

  const userIds = useMemo(() => people.map((p) => p.userId), [people]);
  const nameMap = useMemo(() => new Map(people.map((p) => [p.userId, p.name])), [people]);
  const nameOf = (id: string) => nameMap.get(id) ?? "Ukjent";
  const metaOf = (id: string) => projectMeta.get(id);

  const prevPeriod = shiftPeriod(period, -1);
  const personOpts = { userId: selectedUserId };

  const leaderboard = calcPersonLeaderboard(projects, owners, salesUnits, period, userIds);
  const totalAccuracy = feilbudsjettering(projects, owners, period, personOpts);
  const sellerAccuracy = calcSellerBudgetAccuracy(projects, owners, period, prevPeriod, userIds);
  const topProjects = calcTopProjectsByBf(projects, owners, period, personOpts);
  const worstProjects = calcWorstBudgetProjects(projects, owners, period, personOpts);
  const pipelineTotal = pipelineBf(projects, owners, personOpts);
  const pipelinePerPerson = calcPipelinePerPerson(projects, owners, userIds);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <LeaderboardPanel
          entries={leaderboard}
          nameOf={nameOf}
          highlightUserId={selectedUserId}
          isLoading={isLoading}
        />
        <FeilbudsjetteringPanel
          total={totalAccuracy}
          sellers={sellerAccuracy}
          nameOf={nameOf}
          isLoading={isLoading}
        />
        <PipelinePanel
          teamTotal={pipelineTotal}
          perPerson={pipelinePerPerson}
          nameOf={nameOf}
          isLoading={isLoading}
        />
      </div>
      <BestWorstProjectsPanel
        top={topProjects}
        worst={worstProjects}
        metaOf={metaOf}
        isLoading={isLoading}
      />
      <OffersInPipePanel nameOf={nameOf} enabled={offerPipelineEnabled} />
      <SalesUnitHourlyRatePanel period={period} enabled={salesUnitHourlyRateEnabled} />
      <ProjectLeadHourlyRatePanel period={period} enabled={projectLeadHourlyRateEnabled} />
    </div>
  );
}
